import React, { useState } from "react";
import { GraphQlDataGrid } from "../../../../components/common/grid/GraphQlDataGrid";
import { useNavigate } from "react-router-dom";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
	Alert,
	Breadcrumbs, Button, CircularProgress,
	IconButton,
	Link,
	Tooltip,
	Typography,
} from "@mui/material";
import DataObjectIcon from "@mui/icons-material/DataObject";
import { graphQLServices } from "../../../../services/GraphQLServices";
import {GET_JOB_RUNS} from "../graphql/queries/gqlGetJobRuns";
import {GenericModal} from "../generic-modal/GenericModal";
import { CodeBlock } from "../../../../components/common/CodeBlock";
import {
	createQueryParameterString,
	getQueryParameter,
} from "../../../../libraries/util/QueryParameterHelper";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import moment from "moment";
import {ConfirmationDialog} from "../../../../components/common/confirmation-dialog/ConfirmationDialog";
import {RUN_JOB} from "../graphql/mutations/gqlRunJob";
import {toast} from "react-toastify";
import {JobRunStatus} from "./JobRunStatus";
import {graphql} from "cm6-graphql";
import {FilesCard} from "../job/Files";

export const JobRuns: React.FC = (props) => {
	const [graphqlQuery, setGraphqlQuery] = useState("");
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const [manageFiles, setManageFiles] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>();
	const navigate = useNavigate();
	const jobId: number = +(getQueryParameter("jobId") ?? "0");
	const jobDefinitionId: number = +(
		getQueryParameter("jobDefinitionId") ?? "0"
	);

	const columns: GridColDef[] = [
		{
			field: "details",
			headerName: "",
			width: 140,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<>
						<IconButton
							aria-label="View GraphQl Query"
							onClick={() => setGraphqlQuery(params.row.graphQlQuery)}
						>
							<Tooltip title={"View GraphQl Query"}>
								<DataObjectIcon />
							</Tooltip>
						</IconButton>
						<IconButton
							aria-label="View Steps"
							onClick={() =>
								navigate(
									`/querytool/jobs/runs/steps${createQueryParameterString({
										jobRunId: params.row.id,
										jobId,
										jobDefinitionId,
									})}`
								)
							}
						>
							<Tooltip title={"View Steps"}>
								<DensitySmallIcon />
							</Tooltip>
						</IconButton>
					</>
				);
			},
		},
		{ field: "id", headerName: "ID", width: 70 },
		{
			field: "start",
			headerName: "Start",
			width: 200,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<>
						{params.row.start &&
							moment(params.row.start).format("DD-MM-YYYY HH:mm:ss")}
					</>
				);
			},
		},
		{
			field: "finish",
			headerName: "Finish",
			width: 200,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<>
						{params.row.finish &&
							moment(params.row.finish).format("DD-MM-YYYY HH:mm:ss")}
					</>
				);
			},
		},

		{
			field: "description",
			headerName: "Description",
			width: 200,
			renderCell: (params: GridRenderCellParams) => {
				return <>{params.row.description ?? params.row.job.description}</>;
			},
		},
		{field: "steps", headerName: "Steps", width: 500,disableColumnMenu: true, sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<>
						<JobRunStatus jobSteps={params.row.job.jobDefinition.jobSteps} jobRunSteps={params.row.jobStepRuns} />
					</>
				);
			},
		},
	];
	
	const buttons = (jobId: number): React.ReactNode => {
		return (
			<>
				<ConfirmationDialog
					onSuccess={() => runJob(jobId)}
					dialogTitle={"Are you sure?"}
					buttonTitle={"Start New Run"}
					buttonProps={{ variant: "outlined" }}
					dialogDescription={
						"This will start this Job."
					}
				/>
				<Button variant="outlined" onClick={() => setManageFiles(true)}>Manage Job Files</Button>
			</>
		);
	};
	
	const runJob = (jobId: number) => {
		setSubmitLoading(true);
		setErrorMessage("");
		graphQLServices.querytoolClient
			.mutate({
				mutation: RUN_JOB,
				variables: {
					jobId: jobId,
				},
			})
			.then((result) => {
				toast.success("Job Started");
			})
			.catch(reason => {
				setErrorMessage(JSON.stringify(reason));
			})
			.finally(() => {
				setSubmitLoading(false);
			});
	};
	return (
		<>
			{submitLoading && <CircularProgress />}
			{errorMessage &&
				<Alert severity={"error"}>{errorMessage}</Alert>
			}
			{!submitLoading &&
				<>
					<Breadcrumbs aria-label="breadcrumb">
						<Link underline="hover" color="inherit" href="/querytool/jobdefinitions">
					QueryTool
						</Link>
						<Link underline="hover" color="inherit" href="/querytool/jobdefinitions">
					Job Definitions
						</Link>
						<Link
							underline="hover"
							color="inherit"
							href={`/querytool/jobs${createQueryParameterString({
								jobDefinitionId,
							})}`}
						>
					Jobs
						</Link>
						<Typography color="text.primary">Job Runs</Typography>
					</Breadcrumbs>
					
					<GraphQlDataGrid
						enableHeaderFilter
						hideExportButton
						query={GET_JOB_RUNS}
						graphFunction={"jobRun"}
						client={graphQLServices.querytoolClient}
						rowsPerPageOptions={[10, 20, 50]}
						initialPageSize={10}
						columns={columns}
						defaultSortModel={[
							{
								field: "id",
								sort: "desc",
							},
						]}
						rows={[]}
						queryVariables={{ jobId: jobId }}
						customButtons={[buttons(jobId)]}
					/>
					{jobId && <ConfirmationDialog
						onSuccess={() => runJob(jobId)}
						dialogTitle={"Are you sure?"}
						buttonTitle={"Start Job"}
						buttonProps={{ variant: "outlined" }}
						dialogDescription={
							"This will start this Job."
						}
					/>}
					<GenericModal open={manageFiles} onClose={() => setManageFiles(false)} title={"Files"}>
						<FilesCard id={jobId} type={"JOB"} allowUpload={true}/>
					</GenericModal>
					<GenericModal
						open={!!graphqlQuery}
						onClose={() => setGraphqlQuery("")}
						title={"GraphQl Query"}
					>
						<CodeBlock name={"GraphQl Query"} syntax={graphql()}>
							{graphqlQuery}
						</CodeBlock>
					</GenericModal>
				</>
			}
		</>
	);
};
